import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  MultiSelect,
  NumberInput,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconClipboardCheck, IconPlus, IconTrash } from "@/lib/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import {
  createExaminationType,
  deleteExaminationType,
  type ExaminationTypeCreatePayload,
  listExaminationTypes,
} from "@/lib/api/references_full";
import { useAuthStore } from "@/stores/auth";

const SPECIALTIES = [
  "therapist",
  "neurologist",
  "ophthalmologist",
  "ent",
  "surgeon",
  "gynecologist",
  "urologist",
  "dentist",
  "psychiatrist_narcologist",
  "dermatovenerologist",
  "cardiologist",
  "radiologist",
  "laboratory",
];

export function ExaminationTypesPage() {
  const { t } = useTranslation();
  const canEdit = useAuthStore((s) => s.hasPermission("reference.update"));
  const [opened, { open, close }] = useDisclosure(false);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["examination-types-ref"],
    queryFn: listExaminationTypes,
  });

  const form = useForm<ExaminationTypeCreatePayload>({
    initialValues: {
      code: "",
      name: "",
      default_price: 0,
      currency: "UZS",
      default_validity_days: 365,
      required_specialties: [],
      required_lab_tests: [],
      required_functional: [],
      notes: "",
    },
  });

  const createMutation = useMutation({
    mutationFn: createExaminationType,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["examination-types-ref"] });
      notifications.show({ color: "green", message: t("references.examtype.created") });
      form.reset();
      close();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteExaminationType,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["examination-types-ref"] }),
  });

  return (
    <Stack gap="lg">
      <PageHeader
        title={t("references.examination_types")}
        subtitle={data ? `${data.total} ${t("references.examtype.name")}` : undefined}
        back
        actions={
          canEdit && (
            <Button leftSection={<IconPlus size={16} strokeWidth={1.8} />} onClick={open} radius="xl">
              {t("references.examtype.create")}
            </Button>
          )
        }
      />

      <Card padding={0}>
        <Table.ScrollContainer minWidth={760}>
          <Table striped highlightOnHover verticalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("references.examtype.code")}</Table.Th>
                <Table.Th>{t("references.examtype.name")}</Table.Th>
                <Table.Th>{t("references.examtype.price")}</Table.Th>
                <Table.Th>{t("references.examtype.validity")}</Table.Th>
                <Table.Th>{t("references.examtype.specialties")}</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text c="dimmed" ta="center" py="md">
                      {t("common.loading")}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {data?.items.length === 0 && !isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <EmptyState
                      icon={<IconClipboardCheck size={32} strokeWidth={1.5} />}
                      title={t("references.examtype.empty")}
                    />
                  </Table.Td>
                </Table.Tr>
              )}
              {data?.items.map((tp) => (
                <Table.Tr key={tp.id}>
                  <Table.Td>
                    <Text ff="monospace" fw={600} size="sm">
                      {tp.code}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fw={500} size="sm">
                      {tp.name}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {tp.default_price.toLocaleString()} {tp.currency}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color="blue" radius="sm">
                      {tp.default_validity_days} {t("references.examtype.days")}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      {tp.required_specialties.map((s) => (
                        <Badge key={s} variant="light" color="gray" size="sm" radius="sm">
                          {t(`users.specialties.${s}`, { defaultValue: s })}
                        </Badge>
                      ))}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    {canEdit && tp.is_active && (
                      <Tooltip label={t("common.delete")}>
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          onClick={() => deleteMutation.mutate(tp.id)}
                        >
                          <IconTrash size={16} strokeWidth={1.8} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      <Modal opened={opened} onClose={close} title={t("references.examtype.create")} size="md">
        <form onSubmit={form.onSubmit((v) => createMutation.mutate(v))}>
          <Stack>
            <TextInput
              label={t("references.examtype.code")}
              placeholder={t("placeholder.examination_code")}
              required
              {...form.getInputProps("code")}
            />
            <TextInput
              label={t("references.examtype.name")}
              placeholder={t("placeholder.examination_name")}
              required
              {...form.getInputProps("name")}
            />
            <Group grow>
              <NumberInput
                label={t("references.examtype.price")}
                placeholder={t("placeholder.uzs")}
                min={0}
                {...form.getInputProps("default_price")}
              />
              <NumberInput
                label={t("references.examtype.validity")}
                min={1}
                suffix=" days"
                {...form.getInputProps("default_validity_days")}
              />
            </Group>
            <MultiSelect
              label={t("references.examtype.specialties")}
              placeholder={t("placeholder.select_specialist")}
              data={SPECIALTIES.map((s) => ({
                value: s,
                label: t(`users.specialties.${s}`),
              }))}
              {...form.getInputProps("required_specialties")}
            />
            <Textarea
              label={t("references.examtype.notes")}
              placeholder={t("placeholder.notes")}
              {...form.getInputProps("notes")}
            />
            <Group justify="flex-end" mt="sm">
              <Button variant="subtle" color="gray" onClick={close}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" loading={createMutation.isPending}>
                {t("common.create")}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
